import React from 'react';
import * as Sentry from '@sentry/browser';
import {
  toast,
} from 'react-toastify';
import i18n from 'i18next';
import MobileDetect from 'mobile-detect';

import {
  api,
} from '@gymcloud/core';

import {
  urls,
} from 'web-urls';
import {
  history,
} from 'web-history';

import config from 'web-config';

const env = process.env.TARGET_ENV;

export function initialRedirect(hasToken: boolean) {
  const md = new MobileDetect(window.navigator.userAgent);
  const { location } = window;

  const isOldUrl = location.pathname === '/' && location.hash.startsWith('#');
  const pathname = isOldUrl
    ? `/${location.hash.replace('#', '')}`
    : location.pathname;
  const authRoutes = [
    '/login',
    '/forgot-password',
    '/reset',
    '/confirm',
  ];

  if (md.mobile()) {
    const privateRoutes = [
      ...authRoutes,
      '/welcome',
      '/personal_workouts',
      '/workout_exercises',
      '/personal_programs',
      '/events',
      '/workout_events',
      '/users',
      '/payments',
      '/products',
      '/marketplace',
      '/videos',
      '/billing',
      '/exercises_folder',
      '/workout_templates_folder',
      '/warmups_folder',
      '/cooldowns_folder',
      '/program_templates_folder',
      '/clients',
      '/client_groups',
      '/messages',
      '/calendar',
      '/exercises',
      '/workout_templates',
      '/warmups',
      '/cooldowns',
      '/program_templates',
      '/personal_properties',
      '/search',
      '/tags',
      '/documents',
      '/documents_folder',
      '/personal_exercises',
      '/personal_warmups',
      '/personal_workouts',
      '/personal_cooldowns',
      '/personal_programs',
      '/personal_exercises',
      '/personal_warmups',
      '/personal_cooldowns',
      '/account',
      '/documents',
    ];

    if (pathname === '/' || privateRoutes.indexOf(pathname) !== -1) {
      history.push(urls.useApp);
    } else if (isOldUrl) {
      history.push(pathname);
    }
  } else if (pathname === '/') {
    history.push(urls.dashboardPage);
  } else if (hasToken && authRoutes.indexOf(pathname) !== -1) {
    history.push('/welcome');
  } else if (isOldUrl) {
    history.push(pathname);
  }
}

export function throwError(err) {
  if (env !== 'development') {
    if (err.request) {
      Sentry.withScope((scope) => {
        scope.setExtra('url', err.request.responseURL);
        scope.setExtra('response', err.request.response);
        scope.setExtra('responseText', err.request.responseText);
        scope.setExtra('responseStatus', err.request.status);
        scope.setExtra('responseStatusText', err.request.statusText);
        /* It need to have a correct stacktrace */
        /* TODO: stack trace still is not ideal, handle it somehow */
        Sentry.captureException(new Error(err));
      });
    } else {
      Sentry.captureException(err);
    }
  }
}

export function analyticsIdentify(profile) {
  if (env !== 'development') {
    Sentry.configureScope((scope) => {
      scope.setUser({
        id: profile.id,
        email: profile.email,
        namespace: profile.namespace,
      });
    });
    if (window.analytics) {
      window.analytics.identify(profile.id, {
        isPro: profile.is_pro,
        isDummy: profile.is_dummy,
        isTrialing: profile.is_trialing,
        'User Name': `${profile.user_profile.first_name} ${profile.user_profile.last_name}`,
        'User Email': profile.email,
        'User Namespace': profile.namespace,
      });
    }
  }
}

// TODO Refactoring, do better error handling
export function initialApi() {
  const onSuccessRequests = response => response.data;
  const onErrorRequests = error => {
    const status = error?.request?.status;
    const json = error?.response?.data || {};
    let body = json.error || json.errors;
    const { pathname } = error.response?.config.url;
    const localeText = typeof json.error === 'string' && i18n.t(`error.${json.error}`, { defaultValue: null });

    if (localeText) {
      body = localeText;
    } else if (json.error === 'invalid_grant') {
      body = (
        <>
          {'Try again or '}
          <a
            href="https://gymcloud.freshdesk.com/support/tickets/new"
            rel="noopener noreferrer"
            target="_blank"
          >
            contact support here.
          </a>
        </>
      );
    } else if (json.error_description) {
      body = json.error_description;
    } else if (Array.isArray(body)) {
      body = body.join('. ');
    } else if (typeof body === 'object') {
      body = Object.keys(body).map(
        k => `${k}: ${Array.isArray(body[k]) ? body[k].join('. ') : body[k]}`,
      ).join('\n');
    }

    if (pathname === '/subscriptions') {
      if (body.startsWith('No such coupon:')) {
        body = i18n.t('payment.coupon_invalid');
      } else if (body.startsWith('Coupon expired:')) {
        body = i18n.t('payment.coupon_expired');
      }
    }

    // || status === 403 ? #TODO handle codes
    if (status === 401 && window.location.pathname.indexOf(urls.login) === -1) {
      history.push(urls.login);
      api.setAccessToken(null);
      api.clearAdditionalParams();
      localStorage.removeItem(
        'userId',
      );
      localStorage.removeItem(
        'isPro',
      );
      localStorage.removeItem(
        'accessToken',
      );
    } else if (status === 402) {
      history.push(urls.paymentRequired);
    }

    if (body && (typeof body === 'string' || React.isValidElement(body))) {
      toast.error(body);
    }

    throwError(error);
    return Promise.reject(error);
  };
  api.setApiUrl(config.apiUrl);
  api.instance.interceptors.response.use(onSuccessRequests, onErrorRequests);
}
